import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, UserCog, Building2, Upload, Download, AlertTriangle, Shield, RotateCcw, Power, ChevronDown, ChevronRight, Search } from "lucide-react";
import ConfirmTypeDialog from "@/components/ConfirmTypeDialog";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";

const ROLES = [{ v: "admin", l: "Admin" }, { v: "inspector", l: "Inspector" }, { v: "approver", l: "Approver" }, { v: "viewer", l: "Viewer" }, { v: "customer", l: "Customer" }];

export default function UsersAdmin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "viewer" });
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", role: "viewer", password: "" });
  const [assigning, setAssigning] = useState(null);
  const [assignForm, setAssignForm] = useState({ organization_id: "", sub_organization_id: "", assigned_inspector_id: "" });
  const [orgs, setOrgs] = useState([]);
  const [custDetails, setCustDetails] = useState({}); // id -> hydrated customer
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importOpts, setImportOpts] = useState({ create_missing_orgs: true, dry_run: true });
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saOpen, setSaOpen] = useState(false);
  const [saForm, setSaForm] = useState({ name: "", email: "", password: "" });
  const [tenants, setTenants] = useState([]);
  const [openTenants, setOpenTenants] = useState(() => new Set());
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    const [u, o, c] = await Promise.all([
      api.get("/users").then(r => r.data).catch(() => []),
      api.get("/organizations").then(r => r.data).catch(() => []),
      api.get("/customers").then(r => r.data).catch(() => []),
    ]);
    setUsers(u || []);
    setOrgs(o || []);
    const map = {};
    (c || []).forEach(x => { map[x.id] = x; });
    setCustDetails(map);
  }, []);
  useEffect(() => { load(); }, [load]);

  const inspectors = useMemo(() => users.filter(u => u.role === "inspector" || u.role === "admin"), [users]);
  const rootOrgs = useMemo(() => orgs.filter(o => !o.parent_id), [orgs]);
  const subOrgs = useMemo(() => orgs.filter(o => o.parent_id === assignForm.organization_id), [orgs, assignForm.organization_id]);
  const isSuper = user?.role === "super_admin";
  const superAdmins = useMemo(() => users.filter(u => u.role === "super_admin"), [users]);
  const regularUsers = useMemo(() => users.filter(u => u.role !== "super_admin"), [users]);
  const tenantGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const m = new Map();
    for (const u of regularUsers) {
      if (q && !(`${u.name || ""} ${u.email || ""}`.toLowerCase().includes(q))) continue;
      const tid = u.tenant_id || "none";
      if (!m.has(tid)) m.set(tid, { tid, name: u.tenant_name || "No tenant", users: [] });
      m.get(tid).users.push(u);
    }
    return Array.from(m.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [regularUsers, search]);

  if (user && !["admin","super_admin"].includes(user.role)) return <Navigate to="/dashboard" replace />;

  const create = async () => {
    try {
      await api.post("/auth/register", form);
      toast.success("User created");
      setOpen(false); setForm({ name: "", email: "", password: "", role: "viewer" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const createSuperAdmin = async () => {
    try {
      await api.post("/users/super-admin", saForm);
      toast.success("Super admin created");
      setSaOpen(false); setSaForm({ name: "", email: "", password: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const saveEdit = async () => {
    try {
      const body = { name: editForm.name, role: editForm.role };
      if (editForm.email && editForm.email !== editing.email) body.email = editForm.email;
      if (editForm.password) body.password = editForm.password;
      await api.patch(`/users/${editing.id}`, body);
      toast.success("Updated"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = (u) => {
    setConfirmDelete(u);
  };
  const runRemove = async () => {
    try { await api.delete(`/users/${confirmDelete.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openAssign = (u) => {
    const d = custDetails[u.id] || {};
    setAssignForm({
      organization_id: d.organization_id || "",
      sub_organization_id: d.sub_organization_id || "",
      assigned_inspector_id: d.assigned_inspector_id || "",
    });
    setAssigning(u);
  };
  const saveAssign = async () => {
    try {
      await api.patch(`/customers/${assigning.id}`, {
        organization_id: assignForm.organization_id || null,
        sub_organization_id: assignForm.sub_organization_id || null,
        assigned_inspector_id: assignForm.assigned_inspector_id || null,
      });
      toast.success("Customer assignment updated");
      setAssigning(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/customers/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "customers-template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const runImport = async () => {
    if (!importFile) return toast.error("Choose a CSV file");
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", importFile);
      const qp = new URLSearchParams({ create_missing_orgs: importOpts.create_missing_orgs, dry_run: importOpts.dry_run }).toString();
      const { data } = await api.post(`/customers/import?${qp}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      if (!importOpts.dry_run) { toast.success(`Import complete — ${data.created} created, ${data.updated} updated`); load(); }
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  const toggle2FARequired = async (u) => {
    try { await api.patch(`/users/${u.id}/2fa`, { required: !u.twofa_required }); toast.success(u.twofa_required ? "2FA no longer required" : "2FA now required"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const reset2FA = async (u) => {
    try { await api.post(`/users/${u.id}/2fa/reset`); toast.success("2FA reset — user must re-enrol on next login"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggleActive = async (u) => {
    try { const { data } = await api.patch(`/users/${u.id}/active`, { is_active: u.is_active === false }); toast.success(data.is_active ? "Account enabled" : "Account disabled"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };


  const toggleTenant = (tid) => setOpenTenants(prev => { const n = new Set(prev); if (n.has(tid)) n.delete(tid); else n.add(tid); return n; });

  const userRow = (u) => (
    <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`user-row-${u.id}`}>
      <Td className="font-semibold">{u.name}</Td>
      <Td className="font-mono text-gray-600">{u.email}</Td>
      <Td>
        <span className="text-xs font-bold px-2 py-1 uppercase tracking-wider border bg-gray-100 border-gray-300">{u.role}</span>
        {u.role === "customer" && custDetails[u.id] && (custDetails[u.id].organization_name || custDetails[u.id].inspector_name) && (
          <div className="text-[10px] text-gray-500 mt-1 space-x-2">
            {custDetails[u.id].organization_name && <span><Building2 className="w-3 h-3 inline" /> {custDetails[u.id].organization_name}{custDetails[u.id].sub_organization_name ? ` · ${custDetails[u.id].sub_organization_name}` : ""}</span>}
            {custDetails[u.id].inspector_name && <span>· Insp: <b>{custDetails[u.id].inspector_name}</b></span>}
          </div>
        )}
      </Td>
      <Td>
        {u.twofa_enabled ? (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#D1FAE5] text-[#065F46]" data-testid={`sec-2fa-on-${u.id}`}>2FA ON</span>
        ) : (
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${(u.twofa_required || ["admin","approver"].includes(u.role)) ? "bg-[#FEF3C7] text-[#92400E]" : "bg-gray-100 text-gray-400"}`} data-testid={`sec-2fa-off-${u.id}`}>
            {(u.twofa_required || ["admin","approver"].includes(u.role)) ? "2FA REQUIRED" : "2FA off"}
          </span>
        )}
        {u.is_active === false && <span className="ml-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FEE2E2] text-[#991B1B]" data-testid={`sec-disabled-${u.id}`}>DISABLED</span>}
      </Td>
      <Td className="flex items-center gap-3">
        {u.role === "customer" && (
          <button data-testid={`assign-cust-${u.id}`} onClick={() => openAssign(u)} title="Assign org & inspector" className="text-[#01ADEF] hover:text-[#0198D6]"><Building2 className="w-4 h-4" /></button>
        )}
        <button data-testid={`edit-user-${u.id}`} onClick={() => { setEditing(u); setEditForm({ name: u.name, email: u.email, role: u.role, password: "" }); }} className="text-[#01ADEF] hover:text-[#0198D6]"><UserCog className="w-4 h-4" /></button>
        <button data-testid={`req-2fa-${u.id}`} onClick={() => toggle2FARequired(u)} title={u.twofa_required ? "Stop requiring 2FA" : "Require 2FA"} className={u.twofa_required ? "text-[#D97706] hover:text-[#B45309]" : "text-gray-400 hover:text-[#D97706]"}><Shield className="w-4 h-4" /></button>
        {u.twofa_enabled && <button data-testid={`reset-2fa-${u.id}`} onClick={() => reset2FA(u)} title="Reset 2FA (lost device)" className="text-gray-400 hover:text-[#DC2626]"><RotateCcw className="w-4 h-4" /></button>}
        {u.id !== user?.id && <button data-testid={`toggle-active-${u.id}`} onClick={() => toggleActive(u)} title={u.is_active === false ? "Enable account" : "Disable account"} className={u.is_active === false ? "text-[#059669] hover:text-[#047857]" : "text-gray-400 hover:text-[#DC2626]"}><Power className="w-4 h-4" /></button>}
        {u.id !== user?.id && <button data-testid={`delete-user-${u.id}`} onClick={() => remove(u)} className="text-gray-400 hover:text-[#DC2626]"><Trash2 className="w-4 h-4" /></button>}
      </Td>
    </tr>
  );

  return (
    <div className="p-6 md:p-8" data-testid="users-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">Administration</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">User Management</h1>
        </div>
        <div className="flex gap-2">
        <Button data-testid="import-customers-btn" onClick={() => { setImportResult(null); setImportFile(null); setImportOpen(true); }} variant="outline" className="rounded-full border-gray-300"><Upload className="w-4 h-4 mr-2" />Import CSV</Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-user-btn" className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]"><Plus className="w-4 h-4 mr-2" /> New User</Button>
          </DialogTrigger>          <DialogContent className="rounded-md">
            <DialogHeader><DialogTitle className="font-display">Create User</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <Field label="Name *"><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-md" /></Field>
              <Field label="Email *"><Input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-md" /></Field>
              <Field label="Password *"><Input data-testid="user-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-md" /></Field>
              <Field label="Role"><Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}><SelectTrigger className="rounded-md"><SelectValue /></SelectTrigger><SelectContent>{ROLES.map(r => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent></Select></Field>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="save-user" onClick={create} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="user-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or email…" className="rounded-full pl-9" />
        </div>
        {tenantGroups.length > 1 && (
          <div className="flex items-center gap-2" data-testid="group-controls">
            <Button variant="outline" size="sm" className="rounded-full" data-testid="expand-all-btn" onClick={() => setOpenTenants(new Set(tenantGroups.map(g => g.tid)))}>Expand all</Button>
            <Button variant="outline" size="sm" className="rounded-full" data-testid="collapse-all-btn" onClick={() => setOpenTenants(new Set())}>Collapse all</Button>
          </div>
        )}
      </div>

      <div className="space-y-3" data-testid="tenant-groups">
        {tenantGroups.length === 0 && <div className="bg-white border border-gray-200 px-4 py-10 text-center text-gray-500 text-sm">No users found.</div>}
        {tenantGroups.map((g) => {
          const isOpen = search.trim() !== "" || openTenants.has(g.tid) || tenantGroups.length === 1;
          return (
            <div key={g.tid} className="bg-white border border-gray-200" data-testid={`tenant-group-${g.tid}`}>
              <button onClick={() => toggleTenant(g.tid)} data-testid={`tenant-toggle-${g.tid}`} className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 text-left transition-colors">
                <div className="flex items-center gap-3">
                  {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                  <Building2 className="w-4 h-4 text-[#01ADEF]" />
                  <span className="font-display text-lg font-bold tracking-tight">{g.name}</span>
                </div>
                <span className="text-xs font-semibold px-2 py-1 rounded-full bg-gray-100 text-gray-600">{g.users.length} user{g.users.length === 1 ? "" : "s"}</span>
              </button>
              {isOpen && (
                <div className="overflow-x-auto border-t border-gray-200">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-200 bg-gray-50"><Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Security</Th><Th></Th></tr></thead>
                    <tbody data-testid={`users-table-${g.tid}`}>
                      {g.users.map(userRow)}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {isSuper && (
        <div className="mt-8" data-testid="super-admin-section">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#FE5000] mb-1">Restricted · super-admin only</div>
              <h2 className="font-display text-2xl font-bold tracking-tight">Super Admins</h2>
            </div>
            <Button data-testid="new-super-admin-btn" onClick={() => setSaOpen(true)} className="rounded-full bg-[#1B1B1B] hover:bg-black text-white"><Plus className="w-4 h-4 mr-2" /> New Super Admin</Button>
          </div>
          <div className="bg-white border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-gray-200 bg-gray-50"><Th>Name</Th><Th>Email</Th><Th>Security</Th><Th></Th></tr></thead>
              <tbody data-testid="super-admins-table">
                {superAdmins.map((u) => (
                  <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`sa-row-${u.id}`}>
                    <Td className="font-semibold">{u.name}</Td>
                    <Td className="font-mono text-gray-600">{u.email}</Td>
                    <Td>
                      {u.twofa_enabled
                        ? <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#D1FAE5] text-[#065F46]">2FA ON</span>
                        : <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-400">2FA off</span>}
                      {u.is_active === false && <span className="ml-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FEE2E2] text-[#991B1B]">DISABLED</span>}
                    </Td>
                    <Td className="flex items-center gap-3">
                      <button data-testid={`edit-user-${u.id}`} onClick={() => { setEditing(u); setEditForm({ name: u.name, email: u.email, role: u.role, password: "" }); }} className="text-[#01ADEF] hover:text-[#0198D6]"><UserCog className="w-4 h-4" /></button>
                      <button data-testid={`req-2fa-${u.id}`} onClick={() => toggle2FARequired(u)} title={u.twofa_required ? "Stop requiring 2FA" : "Require 2FA"} className={u.twofa_required ? "text-[#D97706] hover:text-[#B45309]" : "text-gray-400 hover:text-[#D97706]"}><Shield className="w-4 h-4" /></button>
                      {u.twofa_enabled && <button data-testid={`reset-2fa-${u.id}`} onClick={() => reset2FA(u)} title="Reset 2FA" className="text-gray-400 hover:text-[#DC2626]"><RotateCcw className="w-4 h-4" /></button>}
                      {u.id !== user?.id && <button data-testid={`toggle-active-${u.id}`} onClick={() => toggleActive(u)} title={u.is_active === false ? "Enable account" : "Disable account"} className={u.is_active === false ? "text-[#059669]" : "text-gray-400 hover:text-[#DC2626]"}><Power className="w-4 h-4" /></button>}
                      {u.id !== user?.id && <button data-testid={`delete-user-${u.id}`} onClick={() => remove(u)} className="text-gray-400 hover:text-[#DC2626]"><Trash2 className="w-4 h-4" /></button>}
                    </Td>
                  </tr>
                ))}
                {superAdmins.length === 0 && <tr><Td className="text-gray-400 text-sm">No super admins yet.</Td></tr>}
              </tbody>
            </table>
          </div>
          <Dialog open={saOpen} onOpenChange={setSaOpen}>
            <DialogContent className="rounded-md">
              <DialogHeader><DialogTitle className="font-display">Create Super Admin</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <Field label="Name *"><Input data-testid="sa-name" value={saForm.name} onChange={(e) => setSaForm({ ...saForm, name: e.target.value })} className="rounded-md" /></Field>
                <Field label="Email *"><Input data-testid="sa-email" type="email" value={saForm.email} onChange={(e) => setSaForm({ ...saForm, email: e.target.value })} className="rounded-md" /></Field>
                <Field label="Password *"><Input data-testid="sa-password" type="password" value={saForm.password} onChange={(e) => setSaForm({ ...saForm, password: e.target.value })} className="rounded-md" /></Field>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-full" onClick={() => setSaOpen(false)}>Cancel</Button>
                <Button data-testid="save-super-admin" onClick={createSuperAdmin} className="rounded-full bg-[#1B1B1B] hover:bg-black text-white">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-md">
          <DialogHeader><DialogTitle className="font-display">Edit User</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Field label="Name"><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="rounded-md" /></Field>
            <Field label="Email"><Input type="email" data-testid="edit-email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="rounded-md font-mono" /></Field>
            <Field label="Role"><Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}><SelectTrigger className="rounded-md"><SelectValue /></SelectTrigger><SelectContent>{ROLES.map(r => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="New Password (optional)"><Input type="password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} className="rounded-md" /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setEditing(null)}>Cancel</Button>
            <Button data-testid="save-user-edit" onClick={saveEdit} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!assigning} onOpenChange={(o) => !o && setAssigning(null)}>
        <DialogContent className="rounded-md" data-testid="assign-cust-dialog">
          <DialogHeader><DialogTitle className="font-display">Assign — {assigning?.name || assigning?.email}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Field label="Organisation">
              <Select value={assignForm.organization_id || "none"} onValueChange={v => setAssignForm({ ...assignForm, organization_id: v === "none" ? "" : v, sub_organization_id: "" })}>
                <SelectTrigger className="rounded-md" data-testid="assign-org"><SelectValue placeholder="— None —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {rootOrgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Sub-organisation">
              <Select value={assignForm.sub_organization_id || "none"} onValueChange={v => setAssignForm({ ...assignForm, sub_organization_id: v === "none" ? "" : v })} disabled={!assignForm.organization_id}>
                <SelectTrigger className="rounded-md" data-testid="assign-sub-org"><SelectValue placeholder={assignForm.organization_id ? "— None —" : "Pick an organisation first"} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {subOrgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Assigned Inspector">
              <Select value={assignForm.assigned_inspector_id || "none"} onValueChange={v => setAssignForm({ ...assignForm, assigned_inspector_id: v === "none" ? "" : v })}>
                <SelectTrigger className="rounded-md" data-testid="assign-inspector"><SelectValue placeholder="— None —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {inspectors.map(i => <SelectItem key={i.id} value={i.id}>{i.name} ({i.role})</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setAssigning(null)}>Cancel</Button>
            <Button data-testid="save-assign-btn" onClick={saveAssign} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) { setImportResult(null); setImportFile(null); } }}>
        <DialogContent className="rounded-md max-w-2xl" data-testid="import-customers-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Bulk Import Customers</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="text-xs text-gray-600 bg-[#F0F7FF] border border-[#BAE6FD] p-3">
              CSV columns: <code className="font-mono font-bold">email</code> (required), <code className="font-mono">name</code>, <code className="font-mono">organization</code>, <code className="font-mono">sub_organization</code>, <code className="font-mono">inspector_email</code>.
              <button onClick={downloadTemplate} data-testid="dl-import-template" className="inline-flex items-center gap-1 ml-2 text-[#01ADEF] hover:text-[#0198D6] font-semibold"><Download className="w-3 h-3" /> Download template</button>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">CSV File *</Label>
              <div className="mt-1 border border-dashed border-gray-300 hover:border-[#01ADEF] transition-colors">
                <input type="file" accept=".csv,text/csv" onChange={e => setImportFile(e.target.files?.[0] || null)} className="hidden" id="import-file-input" data-testid="import-file-input" />
                <label htmlFor="import-file-input" className="block w-full p-6 text-center cursor-pointer hover:bg-gray-50">
                  {importFile ? (
                    <div><div className="font-mono text-sm font-semibold">{importFile.name}</div><div className="text-[11px] text-gray-500 mt-1">{(importFile.size / 1024).toFixed(1)} KB</div></div>
                  ) : (
                    <div><Upload className="w-6 h-6 mx-auto text-gray-400" /><div className="text-sm text-gray-600 mt-2">Click to select a <b>.csv</b> file</div><div className="text-[11px] text-gray-500 mt-0.5">Max 2MB</div></div>
                  )}
                </label>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" data-testid="import-create-orgs" checked={importOpts.create_missing_orgs} onChange={e => setImportOpts({ ...importOpts, create_missing_orgs: e.target.checked })} className="w-4 h-4" />
                Auto-create missing organisations
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" data-testid="import-dry-run" checked={importOpts.dry_run} onChange={e => setImportOpts({ ...importOpts, dry_run: e.target.checked })} className="w-4 h-4" />
                Dry run (validate only, no writes)
              </label>
            </div>
            {importResult && (
              <div className="border border-gray-200 divide-y divide-gray-100" data-testid="import-result">
                <div className="grid grid-cols-4 divide-x divide-gray-100 bg-gray-50">
                  <ResultCell label={importResult.dry_run ? "Would create" : "Created"} value={importResult.created} accent="text-[#059669]" />
                  <ResultCell label={importResult.dry_run ? "Would update" : "Updated"} value={importResult.updated} accent="text-[#01ADEF]" />
                  <ResultCell label="Orgs created" value={importResult.orgs_created} accent="text-[#7C3AED]" />
                  <ResultCell label="Errors" value={importResult.errors?.length || 0} accent={importResult.errors?.length ? "text-[#DC2626]" : "text-gray-700"} />
                </div>
                {importResult.errors?.length > 0 && (
                  <div className="max-h-40 overflow-y-auto divide-y divide-gray-100 text-xs">
                    {importResult.errors.map((e) => (
                      <div key={`ie-${e.row}-${e.email || "noemail"}`} className="px-3 py-2 flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5 text-[#DC2626]" /><span className="font-mono text-[11px] text-gray-500">Row {e.row}</span><span className="font-mono text-[11px]">{e.email || "—"}</span><span className="text-gray-700 flex-1 truncate">{e.error}</span></div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => { setImportOpen(false); setImportResult(null); }}>Close</Button>
            <Button data-testid="run-import-btn" disabled={importing || !importFile} onClick={runImport} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]">
              {importing ? "Processing…" : (importOpts.dry_run ? "Validate CSV" : "Import Customers")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <ConfirmTypeDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        phrase={confirmDelete?.email || ""}
        title="Delete this user?"
        description={`This will permanently delete ${confirmDelete?.name || "this user"} (${confirmDelete?.email || ""}) and revoke all of their access. This cannot be undone.`}
        confirmLabel="Delete user"
        onConfirm={runRemove}
        testidPrefix="delete-user"
      />
    </div>
  );
}

const Field = ({ label, children }) => (<div><Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">{label}</Label><div className="mt-1">{children}</div></div>);
const Th = ({ children }) => <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold text-gray-600">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
const ResultCell = ({ label, value, accent = "" }) => (
  <div className="p-3 text-center">
    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">{label}</div>
    <div className={`font-display text-2xl font-bold tracking-tight ${accent}`}>{value}</div>
  </div>
);

